import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { formatCurrency, formatDate, getStatusStyle } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Target, Users, Calendar, ChevronRight } from 'lucide-react';

const AdminCampaignsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await adminApi.getCampaigns();
      setCampaigns(res.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await adminApi.updateCampaign(campaignId, { status: newStatus });
      toast.success(`Campaign ${newStatus}`);
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Campaigns
            </h1>
            <p className="text-muted-foreground">Manage creator campaigns</p>
          </div>
          <Button className="bg-primary hover:bg-red-600">
            <Plus className="w-5 h-5 mr-2" />
            Create Campaign
          </Button>
        </div>
        
        {/* Campaigns List */}
        <div className="space-y-4">
          {isLoading ? (
            <>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-32 w-full rounded-xl" />
              ))}
            </>
          ) : campaigns.length === 0 ? (
            <div className="bg-card border border-white/10 rounded-xl p-12 text-center">
              <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">No campaigns yet</h3>
              <p className="text-muted-foreground mb-4">Create your first campaign to start</p>
              <Button className="bg-primary hover:bg-red-600">
                Create Campaign
              </Button>
            </div>
          ) : (
            campaigns.map((campaign) => {
              const statusStyle = getStatusStyle(campaign.status);
              const progress = ((campaign.budget_total - campaign.budget_remaining) / campaign.budget_total) * 100;
              
              return (
                <div
                  key={campaign.campaign_id}
                  className="bg-card border border-white/10 rounded-xl p-6"
                  data-testid={`campaign-${campaign.campaign_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Target className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{campaign.name}</h3>
                        <p className="text-sm text-muted-foreground capitalize">
                          {campaign.objective?.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                      {statusStyle.label}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {campaign.description}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Budget Used</span>
                      <span>{formatCurrency(campaign.budget_total - campaign.budget_remaining)} / {formatCurrency(campaign.budget_total)}</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-6 text-sm mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span>{campaign.creators_assigned || 0} creators</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>{formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {campaign.status === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange(campaign.campaign_id, 'paused')}
                      >
                        Pause
                      </Button>
                    )}
                    {campaign.status === 'paused' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange(campaign.campaign_id, 'active')}
                      >
                        Resume
                      </Button>
                    )}
                    {campaign.status === 'draft' && (
                      <Button
                        size="sm"
                        className="bg-primary hover:bg-red-600"
                        onClick={() => handleStatusChange(campaign.campaign_id, 'active')}
                      >
                        Launch
                      </Button>
                    )}
                    <Button size="sm" variant="ghost">
                      View Details
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCampaignsPage;
